import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Colors from "Colors";
import ButtonComponent from "Controls/ButtonComponent";
import TextButton from "./ComponentsLibrary/Button/TextButton";
import wtI18n from "../util/wt-i18n";

// Maps a question-language culture code to the translation key for that
// language's own name. Check-in's resource bundles key language names by their
// English name (e.g. "English": "Engelsk" in the da-DK bundle), not by culture
// code — so unlike wt-nxt (whose `languages` namespace is keyed by culture) we
// translate via this key. Reuses the existing name translations; no new data.
// Keep in sync with Winningtemp.Resources/LanguageRepository.cs. A culture that
// is missing here falls back to the language's native name.
const LANGUAGE_NAME_KEYS = {
	"sv-SE": "Swedish",
	"en-US": "English",
	"da-DK": "Danish",
	"nb-NO": "Norwegian",
	"de-DE": "German",
	"fi-FI": "Finnish",
	"nl-NL": "Dutch",
	"fr-FR": "French",
	"pl-PL": "Polish",
	"ja-JP": "Japanese",
	"nn-NO": "NewNorwegian",
	"bg-BG": "Bulgarian",
	"th-TH": "Thai",
	"ro-RO": "Romanian",
	"it-IT": "Italian",
	"lv-LV": "Latvian",
	"cs-CZ": "Czech",
	"hu-HU": "Hungarian",
	"ar-SA": "Arabic",
	"es-ES": "Spanish",
	"mn-MN": "Mongolian",
	"zh-Hans": "SimplifiedMandarin",
	"zh-Hant": "TraditionalChinese",
	"lt-LT": "Lithuanian",
	"ru-RU": "Russian",
	"uz-Latn-UZ": "Uzbek",
	"et-EE": "Estonian",
	"tr-TR": "Turkish",
	"uk-UA": "Ukrainian",
	"ne-NP": "Nepali",
	"pt-PT": "Portuguese",
	"el-GR": "Greek",
	"hr-HR": "Croatian",
	"sl-SI": "Slovenian",
	"vi-VN": "Vietnamese",
	"hi-IN": "Hindi",
	"ms-MY": "Malay",
	"sk-SK": "Slovak",
	"is-IS": "Icelandic",
	"sq-AL": "Albanian",
};

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${Colors.blackAlpha50};
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1050;
`;

const Card = styled.div`
	position: relative;
	background-color: ${Colors.white};
	border-radius: 8px;
	padding: 24px;
	max-width: 672px;
	width: 90%;
	box-shadow: 0 4px 20px ${Colors.blackAlpha20};
`;

// Matches the repo's modal close affordance (see Question/ModalHeader.jsx):
// a borderless × glyph, faint by default and darker on hover. Dismissing via
// the × means "just for this visit" — the same as clicking the backdrop.
const CloseButton = styled.button`
	position: absolute;
	top: 12px;
	right: 12px;
	background: none;
	border: 0;
	padding: 0;
	font-size: 24px;
	line-height: 1;
	font-weight: 700;
	color: ${Colors.textDarkestGray};
	cursor: pointer;
	opacity: 0.2;
	&:hover {
		opacity: 0.5;
	}
`;

const Title = styled.h4`
	margin: 0 0 12px;
	font-size: 18px;
	font-weight: 700;
	color: ${Colors.textDarkestGray};
`;

const Body = styled.p`
	margin: 0 0 24px;
	font-size: 14px;
	line-height: 1.4;
	color: ${Colors.grayText};
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
`;

// The real WIT-12577 prompt. Rendered in the NEW language, except the
// "Keep {Current}" button which is rendered in the CURRENT language, so each
// option is labelled in the language it represents. Native language names are
// used (exonyms are unavailable for the check-in question cultures).
//
// Three explicit choices (onUseFromNowOn / onKeep / onJustThisVisit) each act
// on the language. The "×" and the backdrop are a non-choice: onDismiss just
// closes the prompt and leaves the page on whatever language it already shows —
// no language change, no survey refetch.
class LanguageChangeModal extends PureComponent {
	constructor(props) {
		super(props);

		this.state = { ready: false };
	}

	componentDidMount() {
		Promise.all([
			wtI18n.ensureBundle(this.props.newCulture),
			wtI18n.ensureBundle(this.props.currentCulture),
		])
			.then(() => this.setState({ ready: true }))
			.catch(() => this.setState({ ready: true }));
	}

	languageEntry(culture) {
		return (this.props.languages || []).find((l) => l.culture === culture);
	}

	// The language's own (native) name — used where a language is named in its
	// own language (the title/new-language references and the Keep button).
	languageName(culture) {
		const match = this.languageEntry(culture);
		return match ? match.name : culture;
	}

	// The language's name written in another language (exonym): the language's
	// name key translated in the target culture `t`. Falls back to the native
	// name if the culture isn't mapped, which keeps today's behaviour rather
	// than breaking.
	exonym(culture, t) {
		const key = LANGUAGE_NAME_KEYS[culture];
		if (key) {
			return t(key);
		}
		const match = this.languageEntry(culture);
		return match ? match.name : culture;
	}

	render() {
		if (!this.state.ready) {
			return null;
		}

		const { newCulture, currentCulture, context } = this.props;
		const tNew = wtI18n.i18n.getFixedT(newCulture);
		const tCurrent = wtI18n.i18n.getFixedT(currentCulture);
		const newName = this.languageName(newCulture);
		const currentName = this.languageName(currentCulture);
		// The body is shown in the new language, so the current language is named
		// in the new language too (exonym): "…currently in Engelsk", not "English".
		const currentInNewLanguage = this.exonym(currentCulture, tNew);

		const body = context === "login"
			? tNew("CheckinLanguagePromptBodyLogin", { newLanguage: newName, currentLanguage: currentInNewLanguage })
			: tNew("CheckinLanguagePromptBodySurveyList", { newLanguage: newName, currentLanguage: currentInNewLanguage });

		return (
			<Overlay onClick={this.props.onDismiss}>
				<Card onClick={(e) => e.stopPropagation()}>
					<CloseButton aria-label="Close" onClick={this.props.onDismiss}>
						<span aria-hidden="true">×</span>
					</CloseButton>
					<Title>{tNew("CheckinLanguagePromptTitle", { language: newName })}</Title>
					<Body>{body}</Body>
					<Actions>
						<TextButton
							textTransform="none"
							color={Colors.nickel}
							hoverColor={Colors.textDarkestGray}
							onClick={this.props.onJustThisVisit}
							label={tNew("CheckinLanguagePromptJustThisVisit")}
						/>
						<ButtonComponent secondaryWhiteRounded onClick={this.props.onKeep}>
							{tCurrent("CheckinLanguagePromptKeep", { language: currentName })}
						</ButtonComponent>
						<ButtonComponent primaryPurpleRounded onClick={this.props.onUseFromNowOn}>
							{tNew("CheckinLanguagePromptUseFromNowOn", { language: newName })}
						</ButtonComponent>
					</Actions>
				</Card>
			</Overlay>
		);
	}
}

LanguageChangeModal.propTypes = {
	context: PropTypes.oneOf(["login", "survey-list"]),
	newCulture: PropTypes.string,
	currentCulture: PropTypes.string,
	languages: PropTypes.array,
	onUseFromNowOn: PropTypes.func,
	onKeep: PropTypes.func,
	onJustThisVisit: PropTypes.func,
	onDismiss: PropTypes.func,
};

export default LanguageChangeModal;
